import { friendlyStreamError } from "./StreamNotes.jsx";
import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import PlanEditor from "./PlanEditor.jsx";
import { sectionLabel, TYPE_TO_SLUG, playthroughBase } from "../../lib/sections.js";

/**
 * Section play-through — the coordinator's panel on a section's editor page
 * (contract v3.4 §12).
 *
 *   none → Plan scenes → planning… → plan editor → Author scenes → authoring…
 *        → script editor (same page as the intro's) → Finalize → ready
 *
 * Renders nothing when the server has section play-throughs switched off
 * (the endpoint 404s).
 */
const STATUS = {
  none:           { label: "Not written",      color: "var(--color-text-tertiary)", bg: "var(--color-bg-subtle)" },
  planning:       { label: "Planning scenes…", color: "var(--color-status-draft)", bg: "#fef3e2" },
  plan_ready:     { label: "Plan ready",        color: "#7c3aed",                    bg: "rgba(139,92,246,0.08)" },
  generating:     { label: "Authoring scenes…",   color: "var(--color-status-draft)",  bg: "#fef3e2" },
  rewriting:      { label: "Re-authoring a scene…", color: "var(--color-status-draft)", bg: "#fef3e2" },
  review_pending: { label: "Draft",             color: "#7c3aed",                    bg: "rgba(139,92,246,0.08)" },
  finalizing:     { label: "Finalizing…",       color: "var(--color-status-draft)",  bg: "#fef3e2" },
  ready:          { label: "Ready",             color: "var(--color-status-live)",   bg: "var(--color-accent-subtle)" },
  failed:         { label: "Failed",            color: "var(--color-critical)",      bg: "var(--color-critical-bg)" },
};
const ACTIVE = new Set(["planning", "generating", "rewriting", "finalizing"]);

export default function SectionPlaythroughPanel({ clientId, sectionType, pronouns = "" }) {
  const base = playthroughBase(clientId, sectionType);
  const label = sectionLabel(sectionType, pronouns);
  const scriptPath = `/coordinator/clients/${clientId}/edit/${TYPE_TO_SLUG[sectionType]}/playthrough-script`;

  const [pt, setPt] = useState(null);
  const [off, setOff] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [showPlan, setShowPlan] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch(base);
      if (res.status === 404) { setOff(true); return; }
      if (!res.ok) return;   // keep last-known state; the poll retries
      setPt(await res.json());
    } catch { /* keep last-known state */ }
  }, [base]);

  useEffect(() => { load(); }, [load]);

  const phase = pt?.plan_status === "planning" ? "planning"
              : pt?.rewriting ? "rewriting" : (pt?.status || "none");
  useEffect(() => {
    if (!ACTIVE.has(phase)) return;
    const id = setInterval(load, 3000);
    return () => clearInterval(id);
  }, [phase, load]);

  async function call(method, path, body) {
    setBusy(true); setError(null);
    try {
      const res = await fetch(`${base}${path}`, {
        method, headers: { "Content-Type": "application/json" },
        body: body === undefined ? undefined : JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setError(data.error || `Request failed (${res.status})`); return null; }
      return data;
    } catch (e) { setError(e.message); return null; }
    finally { setBusy(false); await load(); }
  }

  if (off || !pt) return null;

  const hasScript = !!pt.has_draft;
  const planReady = pt.plan_status === "plan_ready" && pt.plan?.scenes?.length > 0;
  const editingPlan = !ACTIVE.has(phase) && planReady && (!hasScript || showPlan || pt.status === "failed");
  const cfg = STATUS[editingPlan && !hasScript ? "plan_ready" : phase] || STATUS.none;

  return (
    <section style={p.card} aria-label="Section stream">
      <div style={p.head}>
        <div>
          <h2 style={p.title}>Section stream</h2>
          <p style={p.sub}>A few short narrated scenes about {label}, played from this section's page.</p>
        </div>
        <span style={{ ...p.badge, color: cfg.color, background: cfg.bg }}>{cfg.label}</span>
      </div>

      {error && <p style={p.error}>{error}</p>}
      {pt.plan_status === "plan_failed" && pt.plan_error && <p style={p.error}>Couldn't plan scenes: {pt.plan_error}</p>}
      {pt.status === "failed" && pt.error && <p style={p.error}>Authoring failed: {friendlyStreamError(pt.error)}</p>}

      {!planReady && !ACTIVE.has(phase) && (
        <div style={p.row}>
          <p style={p.hint}>Start with a plan of scenes — you can reorder, rename, remove or add before anything is authored.</p>
          <button type="button" style={p.primary} disabled={busy} onClick={() => call("POST", "/plan")}>Plan scenes</button>
        </div>
      )}

      {phase === "planning" && <p style={p.working}>Planning scenes for {label}…</p>}
      {phase === "generating" && <p style={p.working}>Authoring the scenes — this can take a few minutes…</p>}

      {editingPlan && (
        <PlanEditor
          plan={pt.plan}
          citations={pt.citations?.plan}
          focusSection={sectionType}
          busy={busy}
          hasScript={hasScript}
          onSave={async scenes => !!(await call("PUT", "/plan", { scenes }))}
          onWrite={async () => { setShowPlan(false); await call("POST", "/generate", {}); }}
          onSuggestAgain={() => call("POST", "/plan")}
        />
      )}

      {hasScript && !editingPlan && !["generating", "planning"].includes(phase) && (
        <div style={p.row}>
          <p style={p.hint}>
            {pt.status === "ready"
              ? (pt.edited_since_finalized ? "Live — with edits not yet finalized." : "Live — DSPs can play it from this section.")
              : "Written. Review the script, then finalize to make it playable."}
            {(pt.dropped_scenes || []).length > 0 && (
              <> {pt.dropped_scenes.length === 1 ? "One scene was" : `${pt.dropped_scenes.length} scenes were`} left out because nothing it said could be traced to the profile.</>
            )}
          </p>
          <div style={p.btns}>
            {planReady && <button type="button" style={p.ghost} onClick={() => setShowPlan(true)}>Edit plan</button>}
            <Link to={scriptPath} style={p.primaryLink}>Open script →</Link>
          </div>
        </div>
      )}
    </section>
  );
}

const p = {
  card:   { background: "var(--color-surface)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-lg)", padding: "var(--space-6)", display: "flex", flexDirection: "column", gap: "var(--space-4)" },
  head:   { display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "var(--space-4)" },
  title:  { fontSize: "var(--text-sm)", fontWeight: "var(--weight-semibold)", color: "var(--color-text-secondary)", margin: 0 },
  sub:    { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)", margin: "var(--space-1) 0 0" },
  badge:  { fontSize: "var(--text-xs)", fontWeight: "var(--weight-medium)", borderRadius: "var(--radius-full)", padding: "2px var(--space-2)", whiteSpace: "nowrap" },
  row:    { display: "flex", alignItems: "center", justifyContent: "space-between", gap: "var(--space-4)", flexWrap: "wrap" },
  btns:   { display: "flex", alignItems: "center", gap: "var(--space-2)" },
  hint:   { fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", margin: 0, flex: "1 1 280px", lineHeight: "var(--leading-normal)" },
  working:{ fontSize: "var(--text-sm)", color: "var(--color-accent)", fontWeight: "var(--weight-semibold)", margin: 0 },
  error:  { fontSize: "var(--text-sm)", color: "var(--color-critical)", background: "var(--color-critical-bg)", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-3)", margin: 0 },
  primary:{ fontSize: "var(--text-sm)", fontWeight: "var(--weight-semibold)", color: "#fff", background: "var(--color-accent)", border: "none", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-5)", cursor: "pointer" },
  primaryLink: { fontSize: "var(--text-sm)", fontWeight: "var(--weight-semibold)", color: "#fff", background: "var(--color-accent)", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-5)", textDecoration: "none" },
  ghost:  { fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", background: "none", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-4)", cursor: "pointer" },
};
